import axios from 'axios';

export const REQUEST_OUTCOMES = {
  success: 'success',
  failure: 'failure',
  cancelled: 'cancelled',
};

const csrfToken = () => document.getElementsByName('csrf-token')[0].content;

const isCancelled = (error, signal) => (
  signal?.aborted
  || error?.code === 'ERR_CANCELED'
  || error?.name === 'CanceledError'
  || axios.isCancel?.(error)
);

const serializedError = (error) => {
  if (!error || (typeof error !== 'object' && typeof error !== 'function')) {
    return {};
  }
  return JSON.parse(JSON.stringify(error, Object.getOwnPropertyNames(error)));
};

export const normalizeRequestError = (error, {
  fallbackStatus = 520,
  repairIncompleteResponse = true,
} = {}) => {
  const originalResponse = error?.response;
  const status = originalResponse?.status;
  const message = originalResponse?.data?.error;
  let response = originalResponse;

  if (!response || (repairIncompleteResponse && (!status || !message))) {
    response = {
      status: fallbackStatus === null ? status : (status || fallbackStatus),
      data: {
        error: message || 'An unexpected error occurred, please try again.',
      },
    };
  }

  return {
    ...serializedError(error),
    response,
  };
};

const waitForRetry = (delayMs, signal) => new Promise((resolve) => {
  if (signal?.aborted) {
    resolve(false);
    return;
  }

  let timeout;
  const onAbort = () => {
    clearTimeout(timeout);
    resolve(false);
  };
  timeout = setTimeout(() => {
    signal?.removeEventListener('abort', onAbort);
    resolve(true);
  }, delayMs);
  signal?.addEventListener('abort', onAbort, { once: true });
});

export const requestTransport = async (options, {
  maxRetries = 5,
  normalizeError,
  onFailure,
  onRetry,
  onRetryFailed,
  onUnauthorized,
  retries = 0,
  retryDelayMs = 2000,
} = {}) => {
  try {
    const response = await axios({
      ...options,
      headers: {
        ...options.headers,
        'X-CSRF-TOKEN': csrfToken(),
      },
    });
    return {
      outcome: REQUEST_OUTCOMES.success,
      value: response,
    };
  } catch (error) {
    if (isCancelled(error, options.signal)) {
      return { outcome: REQUEST_OUTCOMES.cancelled };
    }

    if (error?.response?.status === 503 && retries <= maxRetries) {
      const shouldRetry = await waitForRetry(retryDelayMs, options.signal);
      if (!shouldRetry) {
        return { outcome: REQUEST_OUTCOMES.cancelled };
      }
      onRetry?.(retries + 1);
      return requestTransport(options, {
        maxRetries,
        normalizeError,
        onFailure,
        onRetry,
        onRetryFailed,
        onUnauthorized,
        retries: retries + 1,
        retryDelayMs,
      });
    }

    if (retries > maxRetries) onRetryFailed?.();
    if (error?.response?.status === 401) onUnauthorized?.(error);
    onFailure?.(error);

    return {
      outcome: REQUEST_OUTCOMES.failure,
      value: normalizeError
        ? normalizeError(error)
        : normalizeRequestError(error),
    };
  }
};
